import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomTextarea } from "@components/Forms";
import { DropIcon, UploadIcon } from "@components/Icons";
import TableForUIPagination from "@components/Tables/TableForUIPagination";
import { CustomSelect } from "@components/UI";
import Breadcrumb from "@components/UI/Breadcrumb";
import SelectComponentModal from "@components/UI/SelectComponentModal";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFetchData } from "@hooks/useFetchData";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { getComponentLicenseConflict, getLicensePolicyResult, getLicensePolicyRule, uppercase } from "@utils/common";
import { attachLicenseAttributes } from "@utils/licenseAttribute";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { COLOR_CONFLICT_FLAG, CHARACTER_LENGTH, TOAST } from "@constants/index";
import ToastConstant from "@configs/react-toastify/snackbar";

const AddPreview = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [licensePolicyList, setLicensePolicyList] = useState([]);

  const { data, error } = useFetchData(
    [queryKeys.licensePoliciesForProject],
    queryKeys.licensePoliciesForProject,
    { method: "GET" },
    {},
    {},
  );
  const [formData, setFormData] = useState({
    previewName: "",
    licensePolicy: "",
    previewComment: "",
  });

  const [fileInfoModified, setFileInfoModified] = useState([]);

  // File group API hook - will be initialized after preview creation
  const [fileGroupNo, setFileGroupNo] = useState(0);
  const { uploadFilesAndGetOnSuccess } = useFileGroupAPI(fileGroupNo);

  const [selectedComponents, setSelectedComponents] = useState([]);
  const [componentLoading, setComponentLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Pagination state for components table
  const [currentComponentPage, setCurrentComponentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  // Modal state
  const [isSelectComponentModalOpen, setIsSelectComponentModalOpen] = useState(false);

  // Table configuration for components
  const componentTableColumns = ["conflictStatus", "component", "cpe", "action"];

  const getLicenseAttribute = useMutationData(
    () => queryKeys.licenseAttributeByLicenseIdentifierBatch,
    { method: "POST" },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error during get data:", error);
        showErrorToast(t("common.get-license-attribute-error"));
      },
    },
  );

  const mapToPreviewComponent = (component) => ({
    id: component.componentSeq,
    projectPreviewComponentSeq: -1,
    componentName: component.componentName,
    componentVersion: component.componentVersion,
    licenseId: component.licenseId,
    licenseIdentifier: component.licenseIdentifier,
    licenseName: component.licenseName,
    licenseTypeCd: component.licenseTypeCd,
    cpe: component.cpe,
    homepageUrl: component.homepageUrl,
    downloadUrl: component.downloadUrl,
    copyright: component.copyright,
    description: component.description,
    remark: component.remark,
    weaknessCd: component.weaknessCd,
    db: false,
    delete: false,
  });

  const handleComponentSelection = async (selectedComponentItems) => {
    setComponentLoading(true);
    try {
      const componentsToTransform = selectedComponentItems.map(mapToPreviewComponent);
      const transformedComponents = await attachLicenseAttributes(
        componentsToTransform,
        (payload) => getLicenseAttribute.mutateAsync(payload),
      );
      setSelectedComponents(transformedComponents);
      setCurrentComponentPage(1);

      // Clear validation error for components when user adds components
      if (validationErrors.components) {
        setValidationErrors((prev) => ({
          ...prev,
          components: undefined,
        }));
      }
    } finally {
      setComponentLoading(false);
    }
  };

  const renderConflictStatus = (value, row) => {
    const currentPolicy = licensePolicyList.find(
      (policy) => (policy.licensePolicySeq?.toString() || policy.licensePolicySeq) === formData.licensePolicy,
    );

    if (!currentPolicy) {
      return (
        <div className="flex items-center justify-start w-full">
          <div className="flex items-center gap-x-0.5">
            <div className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-l-sm ${colors.bgSkeleton}`} />
            <div className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-r-sm ${colors.bgSkeleton}`} />
          </div>
        </div>
      );
    }

    let licensePolicyResult = getLicensePolicyResult(currentPolicy, row.licenseAttribute);
    let policyConflictColor = licensePolicyResult.policyConflictColor;
    let licensePolicyRule = getLicensePolicyRule(currentPolicy.licensePolicyRules, row);

    if (licensePolicyRule != null) {
      policyConflictColor = COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;
      if (licensePolicyRule.licenseUseApprovalCd == "DISAPPROVAL") {
        policyConflictColor = COLOR_CONFLICT_FLAG.POLICY_CONFLICT;
      }
    }

    row.licensePolicyResult = licensePolicyResult;
    let componentConflictResult = getComponentLicenseConflict(selectedComponents, row);
    row.componentConflictResult = componentConflictResult;

    const itemStyle = { border: "none" };
    const policyConflict = { ...itemStyle, backgroundColor: policyConflictColor };
    const componentConflict = {
      ...itemStyle,
      backgroundColor: componentConflictResult.conflict ? COLOR_CONFLICT_FLAG.LICENSE_CONFLICT : COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT,
    };

    return (
      <div className="flex items-center justify-start w-full">
        <div className="flex items-center gap-x-0.5">
          <div
            className="w-8 h-8 flex items-center justify-center text-base font-bold rounded-l-sm"
            style={{ backgroundColor: policyConflict.backgroundColor, borderRight: "none" }}
          >
            <span className={policyConflict.backgroundColor === COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT ? colors.textConflictUndefined : colors.textStaticWhite}>P</span>
          </div>
          <div
            className="w-8 h-8 flex items-center justify-center text-base font-bold rounded-r-sm"
            style={{ backgroundColor: componentConflict.backgroundColor, borderLeft: "none" }}
          >
            <span className={componentConflict.backgroundColor === COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT ? colors.textConflictUndefined : colors.textStaticWhite}>L</span>
          </div>
        </div>
      </div>
    );
  };

  const componentTableColumnConfig = {
    conflictStatus: {
      headerRender: () => t("project.conflict-status"),
      customRender: renderConflictStatus,
    },
    component: {
      headerRender: () => t("common.component"),
      customRender: (value, row) => (
        <div>
          <div className="text-sm">{row.componentName || "Component Name"}</div>
          <div className={`text-xs ${colors.textNeutralMuted}`}>{row.licenseName || row.licenseIdentifier || "License Info"}</div>
        </div>
      ),
    },
    cpe: {
      headerRender: () => t("open-source.detail-cpe"),
      customRender: (value, row) => <div className="text-sm">{row.cpe || ""}</div>,
    },
    action: {
      headerRender: () => t("common.action"),
      customRender: (value, row) => (
        <div className="flex items-center justify-center">
          <button
            onClick={() => handleDeleteComponent(row.id)}
            className={`rounded p-1 ${colors.textDanger} transition-colors ${colors.hoverTextDangerStrong}`}
            title="Delete"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
    },
  };

  const breadcrumbItems = [
    { label: t("project.previews-list"), href: "/project-previews" },
    { label: t("project.project-preview.add-new-preview"), href: "/project-previews/new" },
  ];

  // Handle license policy data from API
  useEffect(() => {
    if (!data?.length) return;

    setLicensePolicyList(data);
    // Auto-select the Default Policy license policy if none is selected
    const defaultPolicy =
      data.find((policy) => policy.globalFlag === "Y") ?? data[0];
    const defaultPolicyValue =
      defaultPolicy.licensePolicySeq?.toString() || defaultPolicy.licensePolicySeq;
    // Only update if current value is different
    if (formData.licensePolicy !== defaultPolicyValue) {
      setFormData((prev) => ({
        ...prev,
        licensePolicy: defaultPolicyValue,
      }));
    }
  }, [data, error]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // API to create project preview
  const createProjectPreview = useMutationData(
    queryKeys.createProjectPreview,
    { method: "POST" },
    {
      onSuccess: async (response) => {
        if (typeof response !== "string") {
          await updateDataInIndexedDb(STORES.PREVIEWS, response, response.projectPreviewSeq);
          showSuccessToast(t("project.add-project-preview-successfully"));
          // Set file group number for file upload
          const groupNo = response.fileGroupNo;
          if (groupNo !== undefined && groupNo !== null) {
            setFileGroupNo(groupNo);
          }

          // Calculate files to upload (same logic as PreviewDetail)
          const validFiles = fileInfoModified.filter((file) => file.fileInfoSeq || file.file);
          const newFiles = validFiles.map((file) => file.file).filter(Boolean);

          if (newFiles?.length > 0 && groupNo !== undefined && groupNo !== null && groupNo >= 0) {
            const uploadFilesData = newFiles.reduce((acc, file, index) => {
              if (file instanceof File) {
                acc[`file_${index}`] = file;
              }
              return acc;
            }, {});

            const formData = new FormData();
            formData.append("targetSeq", response.projectPreviewSeq);
            formData.append("targetTypeCd", "PROJECT_PREVIEW");
            formData.append("fileGroupNo", groupNo);

            Object.entries(uploadFilesData).forEach(([key, file]) => {
              formData.append(key, file, file.name);
            });

            try {
              // Wait for file upload to complete before navigation
              await uploadFilesAndGetOnSuccess.mutateAsync(formData);

              // Navigate to preview detail with flag to refresh documents
              navigate(`/project-previews/${response.projectPreviewSeq}`, {
                state: {
                  shouldPollDocuments: true,
                  shouldRefreshDocuments: true,
                  fileGroupNo: groupNo,
                },
              });
            } catch (uploadError) {
              console.error("Error uploading files:", uploadError);
              // Still navigate even if upload fails
              navigate(`/project-previews/${response.projectPreviewSeq}`, {
                state: {
                  shouldPollDocuments: true,
                  shouldRefreshDocuments: true,
                  fileGroupNo: groupNo,
                },
              });
            }
          } else {
            // No files to upload, navigate immediately
            navigate(`/project-previews/${response.projectPreviewSeq}`, {
              state: {
                shouldPollDocuments: true,
                shouldRefreshDocuments: false,
              },
            });
          }
        } else {
          // Fallback to preview list if no ID in response
          navigate("/project-previews");
        }
      },
      onError: (error) => {
        const errorMsg =
          error.response?.status === 400 && error.response?.data
            ? error.response.data
            : "";
        let subErrMsg = "";
        switch (errorMsg) {
          case "Project preview name already exists":
            subErrMsg = t("project.project-preview.preview-name-already-existed");
            break;
          default:
            subErrMsg = "";
        }
        ToastConstant(TOAST.error, t("project.create-project-preview-failed"), subErrMsg);
      },
    },
  );

  const handleSave = async () => {
    setValidationErrors({});
    const errors = {};

    if (!formData.previewName.trim()) {
      errors.previewName = t("project.project-preview.preview-name-required");
    }

    if (!formData.licensePolicy) {
      errors.licensePolicy = t("project.project-preview.license-policy-required");
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Prepare payload
    const payload = {
      licensePolicySeq: formData.licensePolicy,
      projectPreviewComment: formData.previewComment || "",
      projectPreviewName: formData.previewName,
      projectPreviewStatusCd: "",
      projectPreviewComponents:
        selectedComponents.length > 0
          ? selectedComponents.map((comp) => ({
              projectPreviewComponentSeq: -1,
              componentName: comp.componentName,
              componentVersion: comp.componentVersion,
              licenseId: comp.licenseId,
              licenseIdentifier: comp.licenseIdentifier,
              licenseName: comp.licenseName,
              licenseTypeCd: comp.licenseTypeCd,
              cpe: comp.cpe,
              homepageUrl: comp.homepageUrl,
              downloadUrl: comp.downloadUrl,
              copyright: comp.copyright,
              description: comp.description,
              remark: comp.remark,
              weaknessCd: comp.weaknessCd,
              licenseAttribute: comp.licenseAttribute,
              db: false,
              delete: false,
              licensePolicyResult: comp.licensePolicyResult,
              componentConflictResult: comp.componentConflictResult,
            }))
          : [],
    };

    await createProjectPreview.mutateAsync(payload);
  };

  const handleCancel = () => {
    navigate("/project-previews");
  };

  const handleSelectComponents = () => {
    setIsSelectComponentModalOpen(true);
  };

  const handleDeleteComponent = (componentId) => {
    setSelectedComponents((prev) => {
      const newComponents = prev.filter((comp) => comp.id !== componentId);
      const maxPage = Math.ceil(newComponents.length / itemsPerPage);
      if (currentComponentPage > maxPage && maxPage > 0) {
        setCurrentComponentPage(1);
      }
      return newComponents;
    });

    // Clear validation error for components when user deletes components
    if (validationErrors.components) {
      setValidationErrors((prev) => ({
        ...prev,
        components: undefined,
      }));
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);

    const newFiles = files.map((file, index) => ({
      index: fileInfoModified.length + index,
      fileInfoSeq: null,
      name: file.name,
      file: file, // Store the actual file object
      selected: false,
    }));

    setFileInfoModified((prev) => [...prev, ...newFiles]);
    // Reset the input
    event.target.value = "";
  };

  const handleDeleteFile = (fileId) => {
    setFileInfoModified((prev) => prev.filter((file) => file.fileInfoSeq !== fileId && file.index !== fileId));
  };

  return (
    <div data-testid="add-preview-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`} data-testid="add-preview-header">
        <div className="flex items-center justify-between">
          <div className="items-center space-x-4">
            <Breadcrumb data-testid="add-preview-breadcrumb" items={breadcrumbItems} />
            <h1
              className={`${colors.textPrimary} pt-4 text-2xl font-bold`}
              data-testid="add-preview-page-title"
            >
              {uppercase(t("project.project-preview.add-new-preview"))}
            </h1>
          </div>
          <div className="flex items-center space-x-3 pt-6" data-testid="add-preview-header-actions">
            <OutlineButton
              data-testid="add-preview-cancel-button"
              onClick={handleCancel}
              className={`rounded-lg border px-4 py-1 font-semibold focus:outline-none ${colors.textSecondary} ${colors.borderHoverPrimary} ${colors.textHoverActive}`}
            >
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid="add-preview-save-button"
              onClick={handleSave}
              disabled={createProjectPreview.isPending || !formData.previewName.trim() || !formData.licensePolicy}
            >
              {createProjectPreview.isPending ? t("common.saving") : t("common.save-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`mt-4 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="add-preview-form">
        <div className={`mx-auto max-w-[55rem]`}>
          <div>
            {/* Preview Information Section */}
            <div className="mb-6" data-testid="add-preview-information-section">
              <h2
                className={`${colors.textPrimary} text-xl font-semibold`}
                data-testid="add-preview-information-title"
              >
                {t("project.project-preview.preview-information")}
              </h2>
              <div className="grid gap-6 grid-cols-2">
                {/* Preview Name */}
                <div>
                  <label className={`mb-2 mt-6 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.preview-name")} <span className={colors.textRequired}>*</span>
                  </label>
                  <input
                    data-testid="add-preview-name-input"
                    type="text"
                    maxLength={256}
                    placeholder={t("project.project-preview.preview-name-placeholder")}
                    value={formData.previewName}
                    onChange={(e) => handleInputChange("previewName", e.target.value)}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${
                      validationErrors.previewName
                        ? colors.borderDangerOutLineButton
                        : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                    } ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} transition-colors duration-300`}
                  />
                  {validationErrors.previewName && (
                    <p className={`mt-1 text-xs ${colors.textDanger}`}>{validationErrors.previewName}</p>
                  )}
                </div>

                {/* License Policy */}
                <div>
                  <label className={`mb-2 mt-6 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("navbar.license-policy")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomSelect
                    data-testid="add-preview-license-policy-select"
                    placeholder={!data ? t("common.page-loading") : t("common.please-select")}
                    value={
                      formData.licensePolicy
                        ? {
                            value: formData.licensePolicy,
                            label:
                              licensePolicyList.find(
                                (policy) =>
                                  (policy.licensePolicySeq?.toString() || policy.licensePolicySeq) ===
                                  formData.licensePolicy,
                              )?.policyName ||
                              licensePolicyList.find(
                                (policy) =>
                                  (policy.licensePolicySeq?.toString() || policy.licensePolicySeq) ===
                                  formData.licensePolicy,
                              )?.licensePolicyName ||
                              "Unnamed Policy",
                          }
                        : null
                    }
                    onChange={(option) => {
                      handleInputChange("licensePolicy", option?.value || "");
                      // Clear validation error for license policy when user selects
                      if (validationErrors.licensePolicy) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          licensePolicy: undefined,
                        }));
                      }
                    }}
                    options={
                      licensePolicyList?.map((policy) => ({
                        value: policy.licensePolicySeq?.toString() || policy.licensePolicySeq,
                        label: policy.policyName || policy.licensePolicyName || "Unnamed Policy",
                      })) || []
                    }
                    className={`w-full text-sm font-medium ${validationErrors.licensePolicy ? colors.borderDangerOutLineButton : ""}`}
                    disabled={!data}
                  />
                  {validationErrors.licensePolicy && (
                    <p className={`mt-1 text-xs ${colors.textDanger}`}>{validationErrors.licensePolicy}</p>
                  )}
                  {error && (
                    <p className={`mt-1 text-xs ${colors.textDanger}`}>
                      Failed to load license policies
                    </p>
                  )}
                </div>

                {/* Preview Comment */}
                <div className="md:col-span-4">
                  <label className={`mb-2 mt-1 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.preview-comment")}
                  </label>
                  <CustomTextarea
                    rows={4}
                    placeholder={t("project.project-preview.additional-comments")}
                    value={formData.previewComment}
                    onChange={(e) => handleInputChange("previewComment", e.target.value)}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="add-preview-preview-comment-char-count">
                    {(formData.previewComment || "").length}/{CHARACTER_LENGTH.COMMENT}
                  </p>
                </div>
              </div>
            </div>

            {/* Preview Components List Section */}
            <div className="mb-6">
              <h2 className={`${colors.textPrimary} mb-2 mt-6 font-bold`}>
                {t("project.project-preview.preview-components-list")}
              </h2>
              {/* Display selected components */}
              {selectedComponents.length > 0 ? (
                <div className="mb-4">
                  <div className="flex justify-end items-center mb-4">
                    <button
                      onClick={handleSelectComponents}
                      className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-300 focus:outline-none ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} ${colors.textButton}`}
                    >
                      {t("project.project-preview.select-more-components")}
                    </button>
                  </div>
                  <TableForUIPagination
                    columns={componentTableColumns}
                    columnConfig={componentTableColumnConfig}
                    data={selectedComponents}
                    currentPage={currentComponentPage}
                    setCurrentPage={setCurrentComponentPage}
                    loading={componentLoading}
                    rowKey="projectPreviewComponentSeq"
                    totalPages={Math.ceil(selectedComponents.length / itemsPerPage)}
                    totalData={selectedComponents.length}
                    maxRowsPerPage={itemsPerPage}
                    hasCheckbox={false}
                    hasDeleteColumn={false}
                    onDeleteRow={handleDeleteComponent}
                    showPagination={true}
                    onItemsPerPageChange={() => {}} // Not needed since we use fixed itemsPerPage
                  />
                </div>
              ) : (
                /* Simple table with headers and empty state */
                <div className={`overflow-hidden rounded-lg border ${colors.borderPrimary} ${colors.bgModal}`}>
                  <div className="overflow-x-auto">
                    {/* Table Headers */}
                    <div className={`${colors.bgModal}  flex`}>
                      <div
                        className={`${colors.textSubTitle} px-4 py-3 text-center text-xs font-medium tracking-wider whitespace-nowrap uppercase`}
                      >
                        {t("project.conflict-status")}
                      </div>
                      <div
                        className={`${colors.textSubTitle} px-4 py-3 text-center text-xs font-medium tracking-wider whitespace-nowrap uppercase`}
                      >
                        {t("common.component")}
                      </div>
                      <div
                        className={`${colors.textSubTitle} px-4 py-3 text-center text-xs font-medium tracking-wider whitespace-nowrap uppercase`}
                      >
                        {t("open-source.detail-cpe")}
                      </div>
                      <div
                        className={`${colors.textSubTitle} px-4 py-3 text-center text-xs font-medium tracking-wider whitespace-nowrap uppercase`}
                      >
                        {t("common.action")}
                      </div>
                    </div>
                  </div>

                  {/* Empty state with icon */}
                  <div className="p-6 text-center">
                    <div className="flex flex-col items-center space-y-4">
                      <div className="mb-2">
                        <DropIcon className={`h-12 w-12 ${colors.textActive}`}/>
                      </div>

                      {/* Empty state message */}
                      <div className="text-center">
                        <p className={`${colors.textSecondary} text-sm`}>
                          {t("project.project-preview.no-components-to-show")}
                        </p>
                        <p className={`${colors.textSecondary} text-sm`}>
                          {t("project.project-preview.select-components-to-create-preview")}
                        </p>
                      </div>
                      {/* Select components button with yellow highlight */}
                      <div className="relative">
                        <button
                          onClick={handleSelectComponents}
                          className={`rounded-lg px-4 py-1 font-semibold transition-all duration-1000 ease-in-out focus:outline-none ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} ${colors.textButton}`}
                        >
                          {t("project.project-preview.select-components")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Additional Files Section */}
            <div className={colors.borderPrimary}>
              <h2 className={`${colors.textPrimary} mb-2 mt-6 font-bold`}>
                {uppercase(t("project.project-preview.additional-files"))}
              </h2>

              {/* Uploaded Files */}
              {fileInfoModified.length > 0 && (
                <div className="mb-4">
                  <h3 className={`${colors.textPrimary} text-sm font-medium mb-2`}>{t("common.uploaded-file")}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {fileInfoModified.map((file, index) => (
                      <div
                        key={file.fileInfoSeq || index}
                        className={`flex min-w-0 items-center justify-between gap-2 px-3 py-2 rounded-lg ${colors.bgPrimary} border ${colors.borderPrimary}`}
                      >
                        <span className={`${colors.textPrimary} min-w-0 truncate text-sm`} title={file.name}>
                          {file.name}
                        </span>
                        <button
                          onClick={() => handleDeleteFile(file.fileInfoSeq || index)}
                          className={`shrink-0 ${colors.iconMuted} ${colors.hoverTextDanger} transition-colors`}
                          title={t("common.delete-button")}
                          aria-label={t("common.delete-button")}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload File Button with Drag & Drop */}
              <div
                className={`border-1 border-dashed ${colors.borderActiveDashed} rounded-lg p-4 text-center ${colors.bgDropFiles} transition-all duration-300`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add(colors.borderActiveDashed, colors.bgDropFilesActive);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove(colors.borderActiveDashed, colors.bgDropFilesActive);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove(colors.borderActiveDashed, colors.bgDropFilesActive);

                  const files = Array.from(e.dataTransfer.files);
                  if (files.length > 0) {
                    const newFiles = files.map((file, index) => ({
                      index: fileInfoModified.length + index,
                      fileInfoSeq: null,
                      name: file.name,
                      file: file,
                      selected: false,
                    }));

                    setFileInfoModified((prev) => [...prev, ...newFiles]);
                  }
                }}
              >
                <input type="file" id="file-upload" className="hidden" onChange={handleFileUpload} multiple />
                <label htmlFor="file-upload" className="flex justify-center items-center cursor-pointer">
                  <div className={`flex items-center space-x-2 ${colors.textComparisonLink} ${colors.hoverTextComparisonLinkStrong} transition-colors`}>
                    <UploadIcon className="w-5 h-5" />
                    <span className="font-semibold">{t("common.drop-files-here-or-click-to-upload")}</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Select Component Modal */}
      <SelectComponentModal
        isOpen={isSelectComponentModalOpen}
        onClose={() => setIsSelectComponentModalOpen(false)}
        onSelect={handleComponentSelection}
        selectedComponents={selectedComponents.map((comp) => ({
          componentSeq: comp.id,
          componentName: comp.componentName,
          componentVersion: comp.componentVersion || "1.0.0",
          licenseIdentifier: comp.licenseIdentifier,
          licenseName: comp.licenseName,
          cpe: comp.cpe,
        }))}
        mode="add-preview"
      />
    </div>
  );
};

export default AddPreview;
